/**
 * GET /getmarketcomparison — precio de una unidad vs. propiedades comparables cercanas.
 * Contrato: argenlotapi `docs/frontend/FRONTEND_MARKET_COMPARISON.md`.
 * Ver docs/maps/market-comparison.md.
 *
 * Solo lectura. `ownerUserId` (miembro del espacio) → `?owner_user_id=`. Usa `httpClient`
 * (JWT + reintento con token renovado ante 401, igual que leadsService).
 *
 * Devuelve el envelope de `httpClient` (`_httpOk` / `_httpStatus`). Un fallo de red o
 * timeout vuelve como `{ _httpOk: false, _httpStatus: null }`; el `AbortError` del
 * consumidor se propaga.
 */
import { get } from './httpClient';
import { spaceOwnerQueryParams } from './spaceMembersService';

export const MARKET_ITEM_KINDS = ['user_lot', 'cadastral_lot', 'cad_parcel'];
export const MARKET_OPERATIONS = ['venta', 'alquiler', 'ambas'];
export const MARKET_TERRIO_TYPES = ['lote', 'departamento', 'ph', 'casa', 'comercio'];
/** Opciones de la UI (filtros del informe, T2). */
export const MARKET_RADIUS_OPTIONS = [500, 800, 1500, 3000];
export const MARKET_MONTHS_OPTIONS = [12, 24, 36];
/** Alquiler: la API busca como mucho 12 meses. */
export const MARKET_RENT_MAX_MONTHS = 12;

const RADIUS_MIN = 100;
const RADIUS_MAX = 3000;
const MONTHS_MIN = 1;
const MONTHS_MAX = 120;

const NETWORK_ERROR = Object.freeze({ _httpOk: false, _httpStatus: null, error: 'No se pudo hablar con el servidor.' });

const trimId = (value) => (value == null ? '' : String(value).trim());

function intInRange(value, min, max) {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  if (!Number.isFinite(n)) return undefined;
  return Math.min(max, Math.max(min, Math.round(n)));
}

/**
 * Arma los params del pedido (sin vacíos). Exportada para tests y para la clave de caché.
 *
 * @param {{ itemKind: string, id: string, radiusM?: number, months?: number,
 *   operation?: string, terrioType?: string }} query
 * @param {string|null} [ownerUserId]
 * @returns {object|null} `null` si falta el registro.
 */
export function buildMarketComparisonParams(query = {}, ownerUserId = null) {
  const itemKind = trimId(query.itemKind);
  const id = trimId(query.id);
  if (!MARKET_ITEM_KINDS.includes(itemKind) || !id) return null;
  const operation = MARKET_OPERATIONS.includes(trimId(query.operation)) ? trimId(query.operation) : undefined;
  const terrioType = MARKET_TERRIO_TYPES.includes(trimId(query.terrioType)) ? trimId(query.terrioType) : undefined;
  const radius = intInRange(query.radiusM, RADIUS_MIN, RADIUS_MAX);
  let months = intInRange(query.months, MONTHS_MIN, MONTHS_MAX);
  if (operation === 'alquiler' && months) months = Math.min(months, MARKET_RENT_MAX_MONTHS);
  return {
    item_kind: itemKind,
    id,
    ...spaceOwnerQueryParams(ownerUserId),
    ...(radius ? { radius_m: radius } : {}),
    ...(months ? { months } : {}),
    ...(operation ? { operation } : {}),
    ...(terrioType ? { terrio_type: terrioType } : {}),
  };
}

/**
 * @param {{ itemKind: 'user_lot'|'cadastral_lot'|'cad_parcel', id: string, radiusM?: number,
 *   months?: number, operation?: 'venta'|'alquiler'|'ambas', terrioType?: string }} query
 * @param {{ ownerUserId?: string|null, signal?: AbortSignal }} [options]
 */
export async function getMarketComparison(query = {}, { ownerUserId = null, signal } = {}) {
  const params = buildMarketComparisonParams(query, ownerUserId);
  if (!params) return { _httpOk: false, _httpStatus: 400, code: 'VALIDATION_ERROR', error: 'Falta el registro.' };
  try {
    return await get('/getmarketcomparison', { params, signal });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    return { ...NETWORK_ERROR };
  }
}
